import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {debounceTime} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {IntFilterComponent} from "./int-filter/int-filter.component";
import {MoneyFilterComponent} from "./money-filter/money-filter.component";
import {DateFilterComponent} from "./date-filter/date-filter.component";
import {AuthenticatedBaseComponent} from '../../authenticated/authenticated-base.component';
import {TableDisplayConfig} from '../../entities/table-display-config.entity';
import {ServiceProviderHub} from '@generated-backend-types';
import {TranslatePipe} from '@ngx-translate/core';
import {environment} from '../../../environments/environment';
import {JsonPipe, NgForOf, NgIf} from '@angular/common';
import {FormGroupComponent} from '../form-group/form-group.component';
import {
  MultiSelectWithStaticOptionsStaticComponent
} from '../multi-select-static-with-static-options/multi-select-with-static-options-static.component';
import {MultiSelectEnumComponent} from '../multi-select-enum/multi-select-enum.component';
import {StaticDateRangeFilterComponent} from './static-date-range-filter/static-date-range-filter.component';
import TableConfig = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Meta.TableConfig;
import TableConfigColumnTypeEnum = ServiceProviderHub.App.Enums.TableConfigColumnTypeEnum;
import TableConfigColumn = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Meta.TableConfigColumn;
import ModelStateEnum = ServiceProviderHub.App.Enums.ModelStateEnum;
import CountryCodeISOAlpha3Enum = ServiceProviderHub.App.Enums.CountryCodeISOAlpha3Enum;
import ServiceProviderTypeEnum = ServiceProviderHub.App.Enums.Resources.ServiceProviderTypeEnum;
import EventPhaseEnum = ServiceProviderHub.App.Enums.Eventmanagement.EventPhaseEnum;
import EventStateEnum = ServiceProviderHub.App.Enums.Eventmanagement.Events.EventStateEnum;
import EventRevenueCostCenterEnum = ServiceProviderHub.App.Enums.Eventmanagement.Events.EventRevenueCostCenterEnum;
import ServiceProviderInquiryStateEnum = ServiceProviderHub.App.Enums.Eventmanagement.Events.ServiceProviderInquiry.ServiceProviderInquiryStateEnum;

@Component({
  selector: 'app-table-filter',
  templateUrl: './table-filter.component.html',
  imports: [
    ReactiveFormsModule,
    IntFilterComponent,
    MoneyFilterComponent,
    DateFilterComponent,
    NgIf,
    NgForOf,
    FormGroupComponent,
    TranslatePipe,
    MultiSelectWithStaticOptionsStaticComponent,
    JsonPipe,
    MultiSelectEnumComponent,
    StaticDateRangeFilterComponent,
  ],
  standalone: true
})
export class TableFilterComponent extends AuthenticatedBaseComponent implements OnInit {
  private toastrService = inject(ToastrService);

  public readonly TableConfigColumnTypeEnum = TableConfigColumnTypeEnum;
  public readonly environment = environment;
  @Output() public settingsClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() public tableDisplayConfigChange: EventEmitter<TableDisplayConfig> = new EventEmitter<TableDisplayConfig>();
  public tableConfig?: TableConfig;
  @Input() public globalFilter?: Record<string, string>;
  @Input() public quickFilterOnly = false;
  @Input() public quickFilterSpacing = false;
  @Input() public rowSpacing = true;
  @Input() public compactView = false;
  @Input() public smallInput = true;
  @Input() public inlineInput = true;
  @Input() public withoutLabel = false;
  protected readonly Validators = Validators;
  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly CountryCodeISOAlpha3Enum = CountryCodeISOAlpha3Enum;
  protected readonly ServiceProviderTypeEnum = ServiceProviderTypeEnum;
  protected readonly EventPhaseEnum = EventPhaseEnum;
  protected readonly EventStateEnum = EventStateEnum;
  protected readonly EventRevenueCostCenterEnum = EventRevenueCostCenterEnum;
  protected readonly ServiceProviderInquiryStateEnum = ServiceProviderInquiryStateEnum;
  private readonly fb = inject(NonNullableFormBuilder);
  public columnFilterForm = this.fb.group({});

  public _tableDisplayConfig?: TableDisplayConfig;

  @Input()
  public set tableDisplayConfig(conf: TableDisplayConfig | undefined) {
    if (!conf) {
      return;
    }
    this.tableConfig = conf.tableConfig;
    this._tableDisplayConfig = JSON.parse(JSON.stringify(conf));

    const values: { [key: string]: string | string[] | null } = {}

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._tableDisplayConfig.tableConfig?.columns?.map(col => {
      if (col.filterable) {
        let value: string | string[] | null
        if (this._tableDisplayConfig && this._tableDisplayConfig.filter[col.name] || conf.filter[col.name] === '') {
          value = conf.filter[col.name];
        } else {
          value = col.default_filter;
        }
        if (typeof value === 'string' && value?.includes(',')) {
          value = value.split(',');
        }

        values[col.name] = value;

        if (!this.columnFilterForm.get(col.name)) {
          this.columnFilterForm.addControl(col.name, this.getFormControlFor(col), {emitEvent: false});
        }
      }
    })

    this.columnFilterForm.reset(values, {emitEvent: true, onlySelf: false})
    this.columnFilterForm.markAsUntouched();
  }

  public resetFilter() {
    this.columnFilterForm.reset();
    this.tableConfig?.columns.forEach((col: TableConfigColumn) => {
      this.columnFilterForm.setControl(col.name, this.getFormControlFor(col));
    })
  }

  public ngOnInit() {

    this.columnFilterForm.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(
      (formValue: Record<string, string[] | string | boolean | undefined | null>) => {

        const oldFilter = JSON.parse(JSON.stringify(this._tableDisplayConfig));

        if (this._tableDisplayConfig) {
          this._tableDisplayConfig.filter = {};
          Object.keys(formValue).forEach((key) => {
            const value = formValue[key];
            if (Array.isArray(value) && this._tableDisplayConfig) {
              if (value.length > 0) {
                this._tableDisplayConfig.filter[key] = value.join(",")
              } else {
                delete this._tableDisplayConfig.filter[key];
              }
            } else if (typeof value === "string" && this._tableDisplayConfig) {
              if (value !== '') {
                this._tableDisplayConfig.filter[key] = value;
              } else {
                delete this._tableDisplayConfig.filter[key];
              }

            } else if (value === null && this._tableDisplayConfig) {

              delete this._tableDisplayConfig.filter[key];
            } else {
              console.error({key, value})
              throw Error('Unhandled Datatype in filter')
            }

          });

          if (!this.areObjectsIdentical(oldFilter, this._tableDisplayConfig) && this.columnFilterForm.valid) {
            this.tableDisplayConfigChange.emit(this._tableDisplayConfig)
          }
          // else {
          //   this.columnFilterForm.markAllAsTouched();
          // }

        }

      }
    )


  }

  private getFormControlFor(col: TableConfigColumn): FormControl {
    const formControl: FormControl = new FormControl(col.default_filter);

    if (col.required_filter) {
      switch (col.type) {
        case TableConfigColumnTypeEnum.MODEL_STATE_STATE_MACHINE:
        case TableConfigColumnTypeEnum.EVENTMANAGEMENT_EVENT_CUSTOMER_OFFER_DEFINITION_STATE_MACHINE:
        case TableConfigColumnTypeEnum.MULTI_LANGUAGE_ATTRIBUTE_DATA:
        case TableConfigColumnTypeEnum.FK_VENUE_ID:
        case TableConfigColumnTypeEnum.FK_VEHICLE_ID:
        case TableConfigColumnTypeEnum.FK_ARTICLE_ID:
        case TableConfigColumnTypeEnum.FK_BOOKING_SLOT_ID:
        case TableConfigColumnTypeEnum.FK_COUNSELLING_DESK_ID:
        case TableConfigColumnTypeEnum.FK_GUEST_ID:
        case TableConfigColumnTypeEnum.FK_GUIDE_ID:
        case TableConfigColumnTypeEnum.FK_LOCATION_ID:
        case TableConfigColumnTypeEnum.FK_SETTLEMENT_ACCOUNT_ID:
        case TableConfigColumnTypeEnum.VC_VEHICLE_BRAND_ENUM:
        case TableConfigColumnTypeEnum.VC_VEHICLE_FUEL_TYPE_ENUM:
        case TableConfigColumnTypeEnum.VC_VEHICLE_CONSTRUCTION_TYPE_ENUM:
        case TableConfigColumnTypeEnum.VC_VEHICLE_SERIES_ENUM:
        case TableConfigColumnTypeEnum.VC_VEHICLE_ICON_TYPE_ENUM:
        case TableConfigColumnTypeEnum.VC_VEHICLE_AVAILABILITY_TYPE_ENUM:
        case TableConfigColumnTypeEnum.GUEST_TITLE_ENUM:
        case TableConfigColumnTypeEnum.GUEST_SALUTATION_ENUM:
        case TableConfigColumnTypeEnum.GUEST_LOCALE_ENUM:
        case TableConfigColumnTypeEnum.ORDER_STATE_ENUM:
        case TableConfigColumnTypeEnum.SALES_CHANNEL_ENUM:
        case TableConfigColumnTypeEnum.SLOT_LANGUAGE_ENUM:
        case TableConfigColumnTypeEnum.SLOT_TYPE_ENUM:
        case TableConfigColumnTypeEnum.VC_BOOKING_STATE_ENUM:
        case TableConfigColumnTypeEnum.VC_BOOKING_TYPE_ENUM:
        case TableConfigColumnTypeEnum.BOOL:
        case TableConfigColumnTypeEnum.ARRAY:
        case TableConfigColumnTypeEnum.STRING:
          formControl.setValidators(col.required_filter ? [Validators.required] : []);
          break;
        default:
          console.error('required filter for column type ' + col.type + ' not implemented');
          this.toastrService.error('required filter for column type ' + col.type + ' not implemented');
      }
    }

    formControl.valueChanges.subscribe(() => formControl.markAsTouched());

    return formControl;
  }

  private areObjectsIdentical(obj1: { [key: string]: unknown }, obj2: { [key: string]: unknown }): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if the number of keys is the same
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check if all keys in obj1 have the same values in obj2
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    return true;
  }
}
